import React, { Suspense } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";

import Header from "../layouts/Header/Header";
import Footer from "../layouts/Footer/Footer";
import BannerCookie from "../layouts/Banner/BannerCookie/BannerCookie";
import SubscribeButton from "layouts/SubscribeButton/BannerCookie/SubscribeButton";
import CanonicalURL from "../components/CanonicalURL/CanonicalURL";
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs";
import "../components/Breadcrumbs/Breadcrumbs.scss";
import "../pages/InsightsPage/InsightsPage.scss";
import "../pages/ForDevelopersPage/ForDevelopersPage.scss";
import { InsightsProvider } from "../context/InsightsContext";

import HomePage from "../pages/HomePage/HomePage";

const AboutUsPage = React.lazy(() =>
  import("../pages/AboutUsPage/AboutUsPage")
);
const ContactUsPage = React.lazy(() =>
  import("../pages/ContactUsPage/ContactUsPage")
);
const ForDevelopersPage = React.lazy(() =>
  import("../pages/ForDevelopersPage/ForDevelopersPage")
);
const ForSuppliersPage = React.lazy(() =>
  import("../pages/ForSuppliersPage/ForSuppliersPage")
);
const ForInvestorsPage = React.lazy(() =>
  import("../pages/ForInvestorsPage/ForInvestorsPage")
);
const ForInsurersPage = React.lazy(() =>
  import("../pages/ForInsurersPage/ForInsurersPage")
);
const WorkForUsPage = React.lazy(() =>
  import("../pages/WorkForUsPage/WorkForUsPage")
);
const EventPage = React.lazy(() => import("pages/EventsPage/EventPage"));
const UnsubscribePage = React.lazy(() =>
  import("../pages/UnsubscribePage/UnsubscribePage")
);

const InsightsHomePage = React.lazy(() =>
  import("../pages/InsightsPage/InsightsPage").then((m) => ({
    default: m.InsightsHomePage,
  }))
);
const InsightsIngishtsPage = React.lazy(() =>
  import("../pages/InsightsPage/InsightsPage").then((m) => ({
    default: m.InsightsIngishtsPage,
  }))
);
const InsightsWeeklyPage = React.lazy(() =>
  import("../pages/InsightsPage/InsightsPage").then((m) => ({
    default: m.InsightsWeeklyPage,
  }))
);
const InsightsEventsPage = React.lazy(() =>
  import("../pages/InsightsPage/InsightsPage").then((m) => ({
    default: m.InsightsEventsPage,
  }))
);
const WeeklyTimelinePage = React.lazy(() =>
  import("../pages/WeeklyTimelinePage/WeeklyTimelinePage")
);
const InsightDetailPage = React.lazy(() =>
  import("../pages/InsightDetailPage/InsightDetailPage")
);
const PoliciesTerms = React.lazy(() =>
  import("../pages/Policies/Policies").then((m) => ({
    default: m.PoliciesTerms,
  }))
);
const PoliciesPrivacy = React.lazy(() =>
  import("../pages/Policies/Policies").then((m) => ({
    default: m.PoliciesPrivacy,
  }))
);
const PoliciesCookies = React.lazy(() =>
  import("../pages/Policies/Policies").then((m) => ({
    default: m.PoliciesCookies,
  }))
);

const AdminApp = React.lazy(() => import("../pages/AdminCMS/AdminApp"));

const ADMIN_HOST_PREFIX = "admin.";

function isAdminHost() {
  return window.location.hostname.startsWith(ADMIN_HOST_PREFIX);
}

function AppLayout({ children }) {
  const location = useLocation();
  const isAdmin =
    isAdminHost() || location.pathname.startsWith("/webridge-insights-editor-panel");

  if (isAdmin) {
    return (
      <Suspense fallback={null}>
        {children}
      </Suspense>
    );
  }

  return (
    <>
      <Header />
      <main style={{ position: "relative" }}>
        <Breadcrumbs />
        <Suspense fallback={null}>
          {children}
        </Suspense>
      </main>
      <BannerCookie />
      <SubscribeButton />
      <Footer />
    </>
  );
}

function AppRoutesInner() {
  if (isAdminHost()) {
    return (
      <AppLayout>
        <Routes>
          <Route path="/*" element={<AdminApp />} />
        </Routes>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Routes>
        <Route path="/about-us" element={<AboutUsPage />} />
        <Route path="/contact-us" element={<ContactUsPage />} />
        <Route path="/services/for-developers" element={<ForDevelopersPage />} />
        <Route path="/services/for-suppliers" element={<ForSuppliersPage />} />
        <Route path="/services/for-investors" element={<ForInvestorsPage />} />
        <Route path="/services/for-insurers" element={<ForInsurersPage />} />
        <Route path="/career" element={<WorkForUsPage />} />
        <Route path="/policies/terms-and-conditions" element={<PoliciesTerms />} />
        <Route path="/policies/privacy-policy" element={<PoliciesPrivacy />} />
        <Route path="/policies/cookies-policy" element={<PoliciesCookies />} />
        <Route path="/gwsce" element={<EventPage />} />
        <Route path="/insights" element={<InsightsHomePage />} />
        <Route path="/insights/articles" element={<InsightsIngishtsPage />} />
        <Route path="/insights/weekly" element={<InsightsWeeklyPage />} />
        <Route path="/insights/weekly/timeline" element={<WeeklyTimelinePage />} />
        <Route path="/insights/events" element={<InsightsEventsPage />} />
        <Route path="/insights/:slug" element={<InsightDetailPage />} />
        <Route path="/unsubscribe" element={<UnsubscribePage />} />
        <Route path="/" element={<HomePage />} />

        <Route path="/webridge-insights-editor-panel/*" element={<AdminApp />} />
      </Routes>
    </AppLayout>
  );
}

export default function AppRoutes() {
  return (
    <BrowserRouter>
      <InsightsProvider>
        <CanonicalURL />
        <AppRoutesInner />
      </InsightsProvider>
    </BrowserRouter>
  );
}
